import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Dices } from "lucide-react";
import { AvatarPreset, Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { useTRPC } from "../lib/api";
import { useSession } from "../state/session";
import { pendingResolvePath, takePendingResolve } from "../lib/pendingResolve";
import { useActive } from "../state/hooks";
import { LoadingLine } from "../components/common";

/**
 * Where a player goes once they have their name: a quest/contact/order QR
 * scanned before joining takes priority (the player came for that code), else
 * the guided tour on first run, else the quests. Shared by the identity reveal
 * (manual join) and the join link, which picks the name before joining.
 */
export function useContinueToGame() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return async (tourCompleted: boolean) => {
    await queryClient.invalidateQueries();
    const pending = takePendingResolve();
    if (pending) {
      const path = pendingResolvePath(pending);
      void navigate({ to: path.to, params: path.params });
    } else if (!tourCompleted) {
      void navigate({ to: "/quests", search: { tour: 1 } });
    } else {
      void navigate({ to: "/quests", search: {} });
    }
  };
}

/**
 * Identity reveal (brief §6): avatar springs in, generated name beneath,
 * unlimited Shuffle (server re-roll, uniqueness enforced), `Let's go` flows
 * into the guided tour on first run. No confetti here — the first celebration
 * is reserved for the first solved quest.
 */
export function IdentityScreen() {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const { active, user, isLoading } = useActive();
  const { locale } = useSession();
  const [identity, setIdentity] = useState<{ displayName: string; avatarPresetId: string } | null>(null);
  const reroll = useMutation(trpc.participant.rerollIdentity.mutationOptions());
  const continueToGame = useContinueToGame();

  useEffect(() => {
    if (!isLoading && !active) void navigate({ to: "/join" });
  }, [isLoading, active, navigate]);

  if (!active) return <LoadingLine />;

  const shown = identity ?? { displayName: active.displayName, avatarPresetId: active.avatarPresetId };

  const shuffle = async () => {
    const next = await reroll.mutateAsync({ eventId: active.eventId, locale });
    setIdentity(next);
  };

  const done = () => continueToGame(!(user && user.tourCompletedAt === null));

  return (
    <main
      data-testid="ec-identity"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-5 px-6 pb-16 text-center"
    >
      <div key={shown.avatarPresetId + shown.displayName} className="ec-bracket-snap">
        <AvatarPreset preset={shown.avatarPresetId} size={96} label={t("ec2.a11y.avatar", { name: shown.displayName })} />
      </div>
      <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg" data-testid="ec-identity-name">
        {shown.displayName}
      </h1>
      <p className="text-[13px] font-medium text-fg-secondary">{t("ec2.identity.caption")}</p>
      <div className="mt-2 flex w-full flex-col gap-3">
        <Button variant="ghost" onClick={() => void shuffle()} disabled={reroll.isPending} data-testid="ec-identity-shuffle">
          <Dices size={20} aria-hidden />
          {t("ec2.identity.shuffle")}
        </Button>
        <Button variant="primary" onClick={() => void done()} data-testid="ec-identity-go">
          {t("ec2.identity.letsGo")}
        </Button>
      </div>
    </main>
  );
}
